// Mock "sample intelligence" data for the Commerce Intelligence module.
// Frontend prototype only — no backend calls. Clearly labelled as sample data in the UI.

export const HERO_STATS = [
  { value: "2.8M", label: "Customer Conversations" },
  { value: "8.2M", label: "Product Reviews" },
  { value: "640K", label: "Ads Analysed" },
  { value: "480K", label: "Products" },
  { value: "31K", label: "Factories" },
  { value: "Daily", label: "Refresh Cycle" },
];

export const MARKETS = [
  { name: "Pets", cravings: "1,284", momentum: "+42%", signal: "Dog travel anxiety" },
  { name: "Beauty", cravings: "982", momentum: "+37%", signal: "Heatless hair styling" },
  { name: "Kitchen", cravings: "746", momentum: "+29%", signal: "Small-space storage" },
  { name: "Travel", cravings: "688", momentum: "+51%", signal: "Carry-on compression" },
  { name: "Baby", cravings: "532", momentum: "+24%", signal: "Mess-free feeding" },
  { name: "Fitness", cravings: "617", momentum: "+33%", signal: "Recovery tools" },
  { name: "Camping", cravings: "471", momentum: "+46%", signal: "Bug-free lighting" },
  { name: "Office", cravings: "398", momentum: "+21%", signal: "Desk cable chaos" },
  { name: "Home", cravings: "1,104", momentum: "+31%", signal: "Cooling sleep" },
  { name: "Outdoor", cravings: "544", momentum: "+39%", signal: "Rugged hydration" },
  { name: "Electronics", cravings: "861", momentum: "+27%", signal: "Cable-free charging" },
  { name: "Wellness", cravings: "723", momentum: "+35%", signal: "Sleep recovery" },
];

const badgeFor = (score) => (score >= 90 ? "Investigate" : score >= 80 ? "Watch" : "Avoid");

// Compact craving factory: [title, score, frequency, growth, intent, competition]
const c = (title, score, frequency, growth, intent, competition, detail) => ({
  id: title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, ""),
  title, score, frequency, growth, intent, competition,
  badge: badgeFor(score),
  detail: detail || null,
});

// ---- Showcase detail (Pets → Dog Hair Everywhere) ----
const DOG_HAIR_DETAIL = {
  customerVoice: [
    "I vacuum every day and it still comes back.",
    "My couch is impossible to clean.",
    "Pet hair gets trapped inside my car seats.",
    "Lint rollers are useless after five minutes.",
  ],
  demand: [
    ["Google Trends", "Rising"],
    ["TikTok", "Rising"],
    ["Pinterest", "Rising"],
    ["Amazon Reviews", "Active"],
  ],
  demandStatus: "Growing",
  solutions: [
    ["Amazon", "31 products"],
    ["Shopify", "14 brands"],
    ["TikTok Shop", "8 sellers"],
    ["Walmart", "12 products"],
  ],
  leaders: ["Chom Chom", "Bissell", "Uproot", "Lilly Brush", "FurDaddy"],
  hates: ["Battery dies", "Too noisy", "Too expensive", "Weak on car seats", "Hard to clean"],
  gaps: [
    "Nobody targets car interiors properly.",
    "Most products focus on couches, not vehicles.",
    "No strong product for long-haired dog owners.",
    "Travel version is underserved.",
  ],
  manufacturing: [
    ["China", "18 factories"],
    ["Vietnam", "4 factories"],
    ["India", "3 factories"],
    ["Average MOQ", "200"],
    ["Average Cost", "$8.70"],
    ["Private Label", "Yes"],
  ],
  economics: [
    ["Factory Cost", "$9"],
    ["Shipping", "$5"],
    ["Packaging", "$1"],
    ["Selling Price", "$39"],
    ["Gross Margin", "61%"],
    ["Target CPA", "$15"],
    ["Break-even ROAS", "1.8"],
  ],
  meta: [
    ["Running Ads", "37"],
    ["Average Price", "$39"],
    ["Top Offer", "Buy 2 Get 1"],
    ["Top Hook", "Pet Hair Gone in 60 Seconds"],
    ["Longest Running", "211 days"],
  ],
  tiktok: [
    ["Organic Videos", "182"],
    ["Paid Videos", "29"],
    ["Creator Type", "Pet mom"],
    ["Average Views", "1.7M"],
    ["Best Hook", "Watch this before you vacuum again"],
  ],
  strategy: {
    dont: "Do not compete on “removing pet hair.”",
    doText: "Compete on removing pet hair from vehicles.",
    target: "SUV owners and dog owners who travel frequently.",
    differentiation: "Bundle a hair remover with a seat protector.",
    angle: "Stop your car seats from becoming a dog hair trap.",
  },
  timeMachine: [
    { title: "6 months ago", text: "Mostly couch-focused pet hair tools. Low Meta competition. Few TikTok videos." },
    { title: "Today", text: "Car interior complaints rising. TikTok videos growing. Mid-level competition." },
    { title: "Next 6–12 months", text: "Vehicle-specific pet cleaning bundles likely to grow. Premium travel angle remains underserved." },
  ],
};

// ---- Cravings by market ----
export const CRAVINGS = {
  Pets: [
    c("Dog Hair Everywhere", 98, "Very High", "+66%", "High", "Medium", DOG_HAIR_DETAIL),
    c("Dog Anxiety in Cars", 95, "High", "+58%", "High", "Low"),
    c("Pet Cooling", 91, "High", "+44%", "Medium", "Medium"),
    c("Litter Box Smell", 88, "High", "+27%", "High", "High"),
    c("Food Spills", 84, "Medium", "+31%", "Medium", "Medium"),
  ],
  Beauty: [
    c("Heatless Hair Styling", 93, "High", "+49%", "High", "Medium"),
    c("Under-Eye Puffiness", 87, "High", "+34%", "High", "High"),
    c("Frizz in Humidity", 82, "Medium", "+22%", "Medium", "High"),
  ],
  Kitchen: [
    c("Small-Space Storage", 90, "High", "+38%", "High", "Medium"),
    c("Oil Splatter Mess", 85, "High", "+26%", "Medium", "High"),
    c("Meal-Prep Fatigue", 79, "Medium", "+19%", "Medium", "Medium"),
  ],
  Travel: [
    c("Carry-On Compression", 94, "High", "+57%", "High", "Low"),
    c("Tangled Cables", 86, "High", "+29%", "Medium", "High"),
    c("Neck Pain on Flights", 81, "Medium", "+24%", "Medium", "High"),
  ],
  Baby: [
    c("Mess-Free Feeding", 92, "High", "+41%", "High", "Medium"),
    c("Night Wake-Ups", 88, "High", "+33%", "High", "Medium"),
    c("Diaper Bag Chaos", 78, "Medium", "+18%", "Medium", "High"),
  ],
  Fitness: [
    c("Muscle Recovery", 90, "High", "+45%", "High", "Medium"),
    c("Home Grip Strength", 83, "Medium", "+28%", "Medium", "Medium"),
    c("Sweat-Proof Earbuds", 80, "Medium", "+21%", "Medium", "High"),
  ],
  Camping: [
    c("Bug-Free Lighting", 91, "High", "+52%", "High", "Low"),
    c("Cold Sleep Setup", 85, "Medium", "+30%", "Medium", "Medium"),
    c("Water Purification", 82, "Medium", "+25%", "Medium", "Medium"),
  ],
  Office: [
    c("Desk Cable Chaos", 87, "High", "+31%", "High", "Medium"),
    c("Back Pain at Desk", 84, "High", "+27%", "High", "High"),
    c("Blue-Light Fatigue", 76, "Medium", "+15%", "Low", "High"),
  ],
  Home: [
    c("Cooling Sleep", 93, "Very High", "+43%", "High", "Medium"),
    c("Dust That Returns", 86, "High", "+28%", "Medium", "High"),
    c("Cluttered Entryway", 80, "Medium", "+20%", "Medium", "Medium"),
  ],
  Outdoor: [
    c("Rugged Hydration", 89, "High", "+39%", "High", "Medium"),
    c("Gear That Rusts", 82, "Medium", "+23%", "Medium", "Medium"),
    c("Trail Blisters", 78, "Medium", "+17%", "Medium", "High"),
  ],
  Electronics: [
    c("Cable-Free Charging", 88, "High", "+30%", "High", "High"),
    c("Overheating Laptops", 83, "Medium", "+24%", "Medium", "High"),
    c("Lost AirPods", 79, "Medium", "+19%", "Medium", "High"),
  ],
  Wellness: [
    c("Sleep Recovery", 92, "High", "+40%", "High", "Medium"),
    c("Screen Anxiety", 84, "High", "+29%", "High", "Medium"),
    c("Posture Slump", 80, "Medium", "+22%", "Medium", "Medium"),
  ],
};

// Deterministic-ish generic detail for cravings without a hand-crafted showcase.
export function buildGenericDetail(craving, marketName) {
  const t = craving.title;
  const hot = craving.score >= 85;
  const s = craving.score;
  return {
    generic: true,
    customerVoice: [
      `I've tried everything and "${t.toLowerCase()}" is still a daily frustration.`,
      "Nothing on the market really solves it — they all overpromise.",
      "Spent way too much on products that stopped working within weeks.",
      `Honestly the most annoying part of ${marketName.toLowerCase()} for me.`,
    ],
    demand: [
      ["Google Trends", hot ? "Rising" : "Steady"],
      ["TikTok", "Rising"],
      ["Pinterest", hot ? "Rising" : "Flat"],
      ["Amazon Reviews", "Active"],
    ],
    demandStatus: hot ? "Growing" : "Emerging",
    solutions: [
      ["Amazon", `${18 + (s % 24)} products`],
      ["Shopify", `${7 + (s % 12)} brands`],
      ["TikTok Shop", `${3 + (s % 9)} sellers`],
      ["Walmart", `${5 + (s % 11)} products`],
    ],
    leaders: [`${marketName} Co`, "NovaLabs", "EverydayGoods", "BrightNest", "Kindly"],
    hates: ["Too expensive", "Breaks quickly", "Overpromised results", "Hard to use", "Poor durability"],
    gaps: [
      `No one owns the premium end of "${t}".`,
      `Products are generic — none specialise for serious ${marketName.toLowerCase()} buyers.`,
      "A clean bundle offer is wide open.",
      "The travel / on-the-go version is underserved.",
    ],
    manufacturing: [
      ["China", `${10 + (s % 12)} factories`],
      ["Vietnam", `${2 + (s % 5)} factories`],
      ["India", `${2 + (s % 4)} factories`],
      ["Average MOQ", "250"],
      ["Average Cost", `$${(6 + (s % 8))}.40`],
      ["Private Label", "Yes"],
    ],
    economics: [
      ["Factory Cost", `$${8 + (s % 6)}`],
      ["Shipping", "$5"],
      ["Packaging", "$1"],
      ["Selling Price", `$${34 + (s % 20)}`],
      ["Gross Margin", `${54 + (s % 12)}%`],
      ["Target CPA", "$16"],
      ["Break-even ROAS", "1.9"],
    ],
    meta: [
      ["Running Ads", `${12 + (s % 30)}`],
      ["Average Price", `$${34 + (s % 20)}`],
      ["Top Offer", "Buy 2 Get 1"],
      ["Top Hook", `The fix for ${t.toLowerCase()}`],
      ["Longest Running", `${90 + (s % 140)} days`],
    ],
    tiktok: [
      ["Organic Videos", `${60 + (s % 160)}`],
      ["Paid Videos", `${10 + (s % 30)}`],
      ["Creator Type", `${marketName} enthusiast`],
      ["Average Views", `${(0.6 + (s % 20) / 10).toFixed(1)}M`],
      ["Best Hook", "You've been doing this wrong"],
    ],
    strategy: {
      dont: `Do not compete on the generic "${t.toLowerCase()}" claim.`,
      doText: `Own a sharp, specific angle within ${marketName.toLowerCase()}.`,
      target: `Serious ${marketName.toLowerCase()} buyers who've already tried the cheap options.`,
      differentiation: "Bundle the core product with a complementary accessory.",
      angle: `The upgrade that finally fixes ${t.toLowerCase()}.`,
    },
    timeMachine: [
      { title: "6 months ago", text: `Early, generic solutions for "${t.toLowerCase()}". Low ad competition.` },
      { title: "Today", text: "Complaints rising, TikTok interest growing, competition still moderate." },
      { title: "Next 6–12 months", text: "A specialised, premium angle looks likely to open up. Move before it saturates." },
    ],
  };
}

export const TEST_TRACKER = [
  { product: "Car Pet Hair Remover Bundle", score: 91, spend: "$300", ctr: "2.8%", cpa: "$14", roas: "2.4", profit: "$180", decision: "Continue", good: true },
  { product: "Cooling Pet Mat", score: 74, spend: "$250", ctr: "1.1%", cpa: "$31", roas: "0.9", profit: "-$90", decision: "Kill", good: false },
  { product: "Heatless Curl Kit", score: 83, spend: "$420", ctr: "2.1%", cpa: "$19", roas: "1.7", profit: "$60", decision: "Continue", good: true },
];

export const LAUNCH_ACTIONS = [
  "Generate Landing Page",
  "Generate Meta Ads",
  "Generate TikTok Ads",
  "Generate UGC Script",
  "Generate Higgsfield Prompt",
  "Generate Supplier RFQ",
];
